import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Trash2, Pencil, X } from "lucide-react";

const ROLE_OPTIONS = [
    { value: "staff", label: "Сотрудник" },
    { value: "head", label: "Зав. отделением" },
    { value: "chairman", label: "Председатель" },
];

const ROLE_LABELS = {
    staff: "Сотрудник",
    head: "Зав. отделением",
    chairman: "Председатель",
};

export default function Admins() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [error, setError] = useState("");
    const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });

    const canManage = user?.role === "chairman" || user?.role === "head";
    const canDelete = user?.role === "chairman";

    const load = () =>
        api.get("/admins").then((r) => {
            setItems(r.data.items || []);
            setLoading(false);
        });

    useEffect(() => {
        load();
    }, []);

    const openCreate = () => {
        setEditing(null);
        setForm({ name: "", email: "", password: "", role: "staff" });
        setError("");
        setShowForm(true);
    };

    const openEdit = (u) => {
        setEditing(u);
        setForm({ name: u.name, email: u.email, password: "", role: u.role });
        setError("");
        setShowForm(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        try {
            if (editing) {
                const payload = { name: form.name, role: form.role };
                if (form.password) payload.password = form.password;
                await api.patch(`/admins/${editing.id}`, payload);
            } else {
                await api.post("/admins", form);
            }
            setShowForm(false);
            await load();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    const remove = async (u) => {
        if (!window.confirm(`Удалить пользователя ${u.email}?`)) return;
        try {
            await api.delete(`/admins/${u.id}`);
            await load();
        } catch (err) {
            alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="p-4 lg:p-10" data-testid="admins-page">
            <header className="mb-6 lg:mb-8 flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-1 lg:mb-2 hidden lg:block">Управление</div>
                    <h1 className="text-2xl lg:text-4xl font-heading font-medium tracking-tight text-slate-900">
                        Администраторы
                    </h1>
                    <p className="text-slate-500 mt-1 lg:mt-2 text-sm lg:text-base">Сотрудники приёмной комиссии с доступом в кабинет</p>
                </div>
                {canManage && (
                    <button
                        onClick={openCreate}
                        data-testid="admins-add-button"
                        className="inline-flex items-center gap-2 bg-brand text-white hover:bg-brand-hover transition-colors rounded-md px-4 py-2.5 font-medium text-sm"
                    >
                        <Plus className="h-4 w-4" /> Добавить
                    </button>
                )}
            </header>

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden overflow-x-auto" data-testid="admins-table">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Имя</th>
                            <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Email</th>
                            <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Роль</th>
                            <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={4} className="px-4 py-6 text-sm text-slate-500">Загрузка...</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={4} className="px-4 py-6 text-sm text-slate-500">Нет администраторов</td></tr>
                        ) : (
                            items.map((u) => (
                                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`admin-row-${u.id}`}>
                                    <td className="px-4 py-3 text-sm text-slate-800 font-medium">{u.name}</td>
                                    <td className="px-4 py-3 text-sm text-slate-600">{u.email}</td>
                                    <td className="px-4 py-3">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-slate-100 text-slate-700 border-slate-200">
                                            {ROLE_LABELS[u.role] || u.role}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex gap-1">
                                            {canManage && (
                                                <button
                                                    onClick={() => openEdit(u)}
                                                    data-testid={`admin-edit-${u.id}`}
                                                    className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                                                    title="Редактировать"
                                                >
                                                    <Pencil className="h-4 w-4" strokeWidth={1.5} />
                                                </button>
                                            )}
                                            {canDelete && u.id !== user?.id && (
                                                <button
                                                    onClick={() => remove(u)}
                                                    data-testid={`admin-delete-${u.id}`}
                                                    className="p-1.5 rounded hover:bg-red-50 text-red-600"
                                                    title="Удалить"
                                                >
                                                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {showForm && (
                <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
                    <div className="bg-white rounded-lg shadow-lg max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="admin-form-modal">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                            <h2 className="font-heading font-medium text-slate-900">
                                {editing ? "Редактировать сотрудника" : "Новый сотрудник"}
                            </h2>
                            <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <form onSubmit={submit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Имя</label>
                                <input
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    data-testid="admin-form-name"
                                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Email</label>
                                <input
                                    type="email"
                                    required
                                    disabled={!!editing}
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    data-testid="admin-form-email"
                                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none disabled:bg-slate-50 disabled:text-slate-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                                    Пароль {editing && <span className="text-slate-400 normal-case font-normal">(оставьте пустым, чтобы не менять)</span>}
                                </label>
                                <input
                                    type="password"
                                    required={!editing}
                                    minLength={6}
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    data-testid="admin-form-password"
                                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Роль</label>
                                <select
                                    value={form.role}
                                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                                    data-testid="admin-form-role"
                                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
                                >
                                    {ROLE_OPTIONS.map((r) => (
                                        <option key={r.value} value={r.value}>{r.label}</option>
                                    ))}
                                </select>
                            </div>

                            {error && (
                                <div className="text-sm bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2" data-testid="admin-form-error">
                                    {error}
                                </div>
                            )}

                            <div className="flex justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowForm(false)}
                                    className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-md"
                                >
                                    Отмена
                                </button>
                                <button
                                    type="submit"
                                    data-testid="admin-form-submit"
                                    className="px-4 py-2 text-sm font-medium bg-brand text-white hover:bg-brand-hover rounded-md transition-colors"
                                >
                                    {editing ? "Сохранить" : "Создать"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
